"use client";

import { useRouter } from "next/navigation";
import { Building2, CircleCheck, CirclePlus, Clock } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { SmoothHeight } from "@/components/loading-skeletons";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { useState } from "react";
import OrganizationCreateDialog from "./organization-create-dialog";
import {
  useMyOrganizations,
  useCurrentOrganization,
  usePendingOrgs,
  useSignedLogo,
} from "@/hooks/spacetime";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./ui/tooltip";
import { Avatar, AvatarFallback } from "./ui/avatar";
import { AccountRailAvatar } from "./account-rail-avatar";
import Image from "next/image";
import { startNavigationFeedback } from "@/lib/navigation-feedback";

const ORG_LOGO_KEY_PREFIX = "organizations/";

export function NavOrganization() {
  const router = useRouter();
  const { isMobile, setOpenMobile } = useSidebar();
  const { organizations, isReady: isOrganizationsReady } = useMyOrganizations();
  const {
    organization,
    isReady: isOrganizationReady,
    selectOrganization,
  } = useCurrentOrganization();
  const { pendingOrgs } = usePendingOrgs();
  const pendingJoinRequest = pendingOrgs[0] ?? null;
  const signedLogo = useSignedLogo(organization?.logo);
  // Same display resolution as settings/general: S3 `organizations/*` keys
  // go through the presigner; built-in avatar paths and full http(s) URLs
  // pass through directly (useSignedLogo returns undefined for local paths).
  const organizationLogo = organization?.logo?.startsWith(ORG_LOGO_KEY_PREFIX)
    ? signedLogo
    : organization?.logo || undefined;
  const [dialogOpen, setDialogOpen] = useState(false);

  const openOrganizationDialog = () => {
    if (isMobile) setOpenMobile(false);
    setDialogOpen(true);
  };

  // Switching orgs lands server-side (`selectOrganization` ->
  // `set_last_selected_organization`; `useCurrentOrganization` picks up the
  // live update) — no manual fetch/refresh here. Clicking the ALREADY
  // selected organization instead navigates to its settings, matching
  // legacy's behavior.
  const handleOrganizationChange = (orgId: string) => {
    if (organization && organization.id === orgId) {
      startNavigationFeedback("/settings/general");
      router.push("/settings/general");
      return;
    }
    selectOrganization(orgId);
  };

  return (
    <SidebarMenu className="items-center">
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="flex max-h-8 w-8 shrink-0 items-center !justify-center !gap-0 overflow-hidden md:p-0"
            >
              <AccountRailAvatar
                kind="organization"
                src={organizationLogo}
                pending={
                  !isOrganizationReady ||
                  (!!organization?.logo?.startsWith(ORG_LOGO_KEY_PREFIX) &&
                    !signedLogo)
                }
                alt={organization?.name || pendingJoinRequest?.name || ""}
                fallback={
                  <Building2 className="size-4 text-muted-foreground" />
                }
              />
              <div className="grid flex-1 text-left text-sm leading-7">
                <span className="truncate font-semibold">
                  {organization?.name || pendingJoinRequest?.name || ""}
                </span>
                <span className="truncate text-xs">
                  {pendingJoinRequest && !organization
                    ? "Pending"
                    : "Organization"}
                </span>
              </div>
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg focus-visible:ring-0"
            side={isMobile ? "bottom" : "right"}
            align="end"
            sideOffset={0}
            collisionPadding={8}
          >
            <DropdownMenuGroup>
              <DropdownMenuLabel>
                {pendingJoinRequest && organizations.length === 0
                  ? "Pending request"
                  : "Your organizations"}
              </DropdownMenuLabel>
              <TooltipProvider
                delayDuration={200}
                disableHoverableContent={true}
              >
                <SmoothHeight>
                  {!isOrganizationsReady ? (
                    <div
                      data-testid="nav-organization-skeleton"
                      className="flex items-center gap-2 px-2 py-1.5"
                    >
                      <Skeleton className="size-7 shrink-0 rounded-lg bg-backgroundAccent" />
                      <div className="flex h-7 flex-1 items-center">
                        <Skeleton className="h-3.5 w-28 bg-backgroundAccent" />
                      </div>
                    </div>
                  ) : pendingJoinRequest && organizations.length === 0 ? (
                    <DropdownMenuItem className="text-secondary" disabled>
                      <Avatar className="size-7 rounded-lg bg-background p-0.5">
                        <AvatarFallback className="rounded-lg">
                          <Building2 className="size-4 text-muted-foreground" />
                        </AvatarFallback>
                      </Avatar>
                      <div className="grid flex-1 text-left leading-5">
                        <span className="truncate font-semibold">
                          {pendingJoinRequest.name}
                        </span>
                        <span className="flex items-center text-xs text-muted">
                          <Clock className="mr-1 size-3" />
                          Pending approval
                        </span>
                      </div>
                    </DropdownMenuItem>
                  ) : (
                    organizations.map((org) => (
                      <DropdownMenuItem
                        className="text-secondary"
                        key={org.id}
                        onClick={() => {
                          if (isMobile) setOpenMobile(false);
                          handleOrganizationChange(org.id);
                        }}
                      >
                        <Image
                          src={org?.logo}
                          alt={org?.name || ""}
                          width={28}
                          height={28}
                          priority
                        />
                        <div className="grid flex-1 text-left leading-7">
                          <span className="flex items-center truncate font-semibold">
                            {org?.name || ""}
                            {org.id === organization?.id && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <CircleCheck
                                    className="ml-1 text-brand-accent"
                                    size={16}
                                  />
                                </TooltipTrigger>
                                <TooltipContent side="top" align="center">
                                  Selected
                                </TooltipContent>
                              </Tooltip>
                            )}
                          </span>
                        </div>
                      </DropdownMenuItem>
                    ))
                  )}
                </SmoothHeight>
              </TooltipProvider>
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={openOrganizationDialog}>
              <CirclePlus />
              <span>Create or join organization</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>

      <OrganizationCreateDialog
        controlledOpen={dialogOpen}
        initialView="selection"
        onOpenChange={setDialogOpen}
        onSuccess={() => {
          if (isMobile) setOpenMobile(false);
        }}
        onJoinRequestSuccess={() => {
          if (isMobile) setOpenMobile(false);
        }}
      />
    </SidebarMenu>
  );
}
